import { useParams } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { LiveEventsProvider } from '../contexts/LiveEventsContext'

// resolveLiveOrg picks the organization the live socket binds to. The org in
// the URL wins, so moving between orgs reconnects. Without one (/admin/...,
// /organizations, /no-organization) a signed-in user falls back to the org
// they last used (the session's orgCode), which keeps the socket up across
// /$org/... <-> /admin/... navigation: same org, same provider prop, no
// teardown. Logged-out routes (/login, MFA...) have no org and no socket.
export function resolveLiveOrg(
    urlOrg: string | undefined,
    isAuthenticated: boolean,
    orgCode: string | null
): string | null {
    if (!isAuthenticated) return null
    return urlOrg || orgCode || null
}

// LiveEventsGate mounts LiveEventsProvider whenever an org is known and the
// user is signed in (the URL param is read loosely: `strict: false` works
// from the ROOT route, which doesn't declare :org itself).
//
// It wraps AppShell (header + sidebar + <Outlet/>) rather than just
// <Outlet/>: the header's LiveStatusIndicator lives ABOVE the outlet, so a
// provider scoped to the routed content could never reach it via context.
// See docs/apis/events.md.
export function LiveEventsGate({ children }: { children: ReactNode }) {
    const { org } = useParams({ strict: false })
    const { isAuthenticated, orgCode } = useAuth()
    const liveOrg = resolveLiveOrg(org, isAuthenticated, orgCode)
    if (!liveOrg) return <>{children}</>
    return <LiveEventsProvider org={liveOrg}>{children}</LiveEventsProvider>
}
